import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getClerkAuthHeaders, getDemoAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  saveDailyLogOffline,
  getPendingDailyLogsByProject,
  generateOfflineId,
  isOfflineId,
  type OfflineDailyLog,
} from "@/lib/offline-storage";
import { triggerSync } from "@/lib/sync-manager";
import { useOnlineStatus } from "./useOnlineStatus";

export interface Photo {
  id: string;
  projectId: string;
  dailyLogId?: string | null;
  costCodeId?: string | null;
  url: string;
  thumbnailUrl?: string | null;
  caption?: string | null;
  takenAt: string;
  locationLat?: number | null;
  locationLng?: number | null;
  createdBy?: string | null;
  createdAt: string;
}

export interface DailyLog {
  id: string;
  projectId: string;
  date: string;
  costCodeId?: string | null;
  costCode?: string | null;
  costCodeName?: string | null;
  weather: {
    temp: number;
    conditions: string;
  };
  crewCount: number;
  workPerformed: string;
  issues?: string;
  createdBy: string;
  createdAt: string;
  syncedAt?: string;
  photos?: Photo[];
  // Offline support fields
  isPending?: boolean;
  syncStatus?: "pending" | "syncing" | "synced" | "error";
  syncError?: string;
}

/**
 * Convert an offline log to the DailyLog format for UI display
 */
function offlineLogToDailyLog(offlineLog: OfflineDailyLog): DailyLog {
  return {
    id: offlineLog.id,
    projectId: offlineLog.projectId,
    date: offlineLog.date,
    weather: {
      temp: offlineLog.weatherTemp || 0,
      conditions: offlineLog.weatherConditions || "",
    },
    crewCount: offlineLog.crewCount || 0,
    workPerformed: offlineLog.workPerformed,
    issues: offlineLog.issues || undefined,
    createdBy: "local", // Will be set by server on sync
    createdAt: offlineLog.createdAt,
    isPending: true,
    syncStatus: offlineLog.syncStatus,
    syncError: offlineLog.syncError,
  };
}

export interface CreateDailyLogData {
  date: string;
  costCodeId?: string;
  weather: {
    temp: number;
    conditions: string;
  };
  crewCount: number;
  workPerformed: string;
  issues?: string;
}

// Transform data for API (temp -> temperature)
function transformWeatherForApi(data: CreateDailyLogData) {
  return {
    date: data.date,
    costCodeId: data.costCodeId,
    weather: {
      temperature: data.weather.temp,
      conditions: data.weather.conditions,
    },
    crewCount: data.crewCount,
    workPerformed: data.workPerformed,
    issues: data.issues,
  };
}

export interface UpdateDailyLogData {
  date?: string;
  costCodeId?: string;
  weather?: {
    temp?: number;
    conditions?: string;
  };
  crewCount?: number;
  workPerformed?: string;
  issues?: string;
}

// API response uses 'temperature', frontend uses 'temp'
interface ApiDailyLog {
  id: string;
  projectId: string;
  date: string;
  costCodeId?: string | null;
  costCode?: string | null;
  costCodeName?: string | null;
  weather?: {
    temperature: number | null;
    conditions: string | null;
  };
  crewCount: number;
  workPerformed: string;
  issues?: string | null;
  createdBy: string;
  createdAt: string;
  syncedAt?: string;
  photos?: Photo[];
}

interface ApiDailyLogsResponse {
  dailyLogs: ApiDailyLog[];
}

interface ApiDailyLogResponse {
  dailyLog: ApiDailyLog;
}

// Transform API response to frontend format (temperature -> temp)
function transformApiLog(apiLog: ApiDailyLog): DailyLog {
  return {
    ...apiLog,
    weather: {
      temp: apiLog.weather?.temperature ?? 0,
      conditions: apiLog.weather?.conditions ?? "",
    },
    issues: apiLog.issues ?? undefined,
    syncStatus: "synced" as const,
  };
}

// Response types for internal use (after transformation)
interface DailyLogResponse {
  dailyLog: DailyLog;
}

// Get all daily logs for a project (merges server + pending offline logs)
export function useDailyLogs(projectId: string) {
  const auth = useAuthConditional();
  const isOnline = useOnlineStatus();

  return useQuery({
    queryKey: ["projects", projectId, "daily-logs"],
    queryFn: async () => {
      // Get pending offline logs first
      const pendingLogs = await getPendingDailyLogsByProject(projectId);
      const pendingDailyLogs = pendingLogs.map(offlineLogToDailyLog);

      // If offline, return only pending logs
      if (!isOnline) {
        return {
          dailyLogs: pendingDailyLogs,
        };
      }

      // If online, fetch from server and merge with pending
      try {
        const headers = CLERK_AVAILABLE
          ? await getClerkAuthHeaders(auth.getToken)
          : getDemoAuthHeaders();

        const serverResponse = await fetchApi<ApiDailyLogsResponse>(
          `/api/projects/${projectId}/daily-logs`,
          { headers }
        );

        // Transform API logs to frontend format
        const serverLogs = serverResponse.dailyLogs.map(transformApiLog);

        // Merge: pending logs first (they're newer), then server logs
        // Filter out server logs that might have same date as pending (to avoid duplicates)
        const pendingDates = new Set(pendingDailyLogs.map((log) => log.date));
        const serverLogsFiltered = serverLogs.filter(
          (log) => !pendingDates.has(log.date) && !isOfflineId(log.id)
        );

        return {
          dailyLogs: [...pendingDailyLogs, ...serverLogsFiltered],
        };
      } catch (error) {
        // If network error, return pending logs only
        console.warn("Failed to fetch server logs, returning offline only:", error);
        return {
          dailyLogs: pendingDailyLogs,
        };
      }
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get a single daily log (checks offline storage first if it's an offline ID)
export function useDailyLog(projectId: string, logId: string) {
  const auth = useAuthConditional();
  const isOnline = useOnlineStatus();

  return useQuery({
    queryKey: ["projects", projectId, "daily-logs", logId],
    queryFn: async () => {
      // If this is an offline ID, check IndexedDB first
      if (isOfflineId(logId)) {
        const { getDailyLogById } = await import("@/lib/offline-storage");
        const offlineLog = await getDailyLogById(logId);

        if (offlineLog) {
          return {
            dailyLog: offlineLogToDailyLog(offlineLog),
          };
        }

        // If not found in offline storage, it may have been synced
        // Try fetching from server if online
        if (!isOnline) {
          throw new Error("Daily log not found in offline storage");
        }
      }

      // Fetch from server (either normal ID or offline ID that was synced)
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const response = await fetchApi<ApiDailyLogResponse>(`/api/projects/${projectId}/daily-logs/${logId}`, {
        headers,
      });

      return {
        dailyLog: transformApiLog(response.dailyLog),
      };
    },
    enabled: auth.isSignedIn && !!projectId && !!logId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if it's an offline log not found error
      if (error instanceof Error && error.message.includes("not found in offline storage")) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// Create a daily log (offline-first: saves locally, syncs when online)
export function useCreateDailyLog(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (data: CreateDailyLogData): Promise<DailyLogResponse> => {
      const offlineId = generateOfflineId();
      const now = new Date().toISOString();

      // Always save to IndexedDB first (offline-first approach)
      const offlineLog = await saveDailyLogOffline({
        id: offlineId,
        projectId,
        date: data.date,
        weatherTemp: data.weather.temp,
        weatherConditions: data.weather.conditions,
        weatherAuto: true,
        crewCount: data.crewCount,
        workPerformed: data.workPerformed,
        issues: data.issues || null,
        createdAt: now,
      });

      // Convert to DailyLog format for immediate UI display
      const dailyLog = offlineLogToDailyLog(offlineLog);

      // If online, try to sync immediately
      if (isOnline) {
        try {
          const headers = CLERK_AVAILABLE
            ? await getClerkAuthHeaders(auth.getToken)
            : getDemoAuthHeaders();

          const serverResponse = await fetchApi<DailyLogResponse>(
            `/api/projects/${projectId}/daily-logs`,
            {
              method: "POST",
              headers,
              body: JSON.stringify({
                ...transformWeatherForApi(data),
                offlineId, // Send offline ID for server-side deduplication
              }),
            }
          );

          // Sync successful - trigger cleanup of offline log
          triggerSync();
          return serverResponse;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error";

          // Check if it's a 409 conflict
          if (errorMessage.includes("409") || errorMessage.includes("already exists")) {
            // 409 means log already exists on server
            // This should be rare now due to server idempotent handling
            // But if it happens, mark it appropriately and let sync manager handle cleanup
            console.warn(`Daily log for ${data.date} already exists on server - will be handled by sync manager`);
            triggerSync();
          } else {
            // Network/auth error - log is saved locally, will sync later
            console.warn("Failed to sync immediately, will retry later:", error);
            // Trigger background sync attempt
            triggerSync();
          }
        }
      } else {
        console.log("Offline: Daily log saved locally, will sync when online");
      }

      // Return the local log (either because offline or sync failed)
      return { dailyLog };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "daily-logs"] });
    },
  });
}

// Update a daily log
export function useUpdateDailyLog(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDailyLogData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<DailyLogResponse>(`/api/projects/${projectId}/daily-logs/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "daily-logs"] });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "daily-logs", variables.id],
      });
    },
  });
}

// Delete a daily log
export function useDeleteDailyLog(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(`/api/projects/${projectId}/daily-logs/${id}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "daily-logs"] });
    },
  });
}
